import { Injectable } from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {BehaviorSubject, catchError, Observable, throwError} from 'rxjs';
import { tap } from 'rxjs/operators';
import {jwtDecode} from 'jwt-decode';
import {BE_API_AUTH_URL, BE_API_URL} from '../constants/app.constants';
import {AppMode, DEFAULT_APP_MODE} from '../models/app-mode.model';
import { UserPlan } from '../models/user-plan.model';
import { MfaStatus } from '../models/user.model';
import { RegistrationAttribution } from './registration-attribution.service';
import { CompanyThemeService } from './company-theme.service';

export interface MfaChallengeResponse {
  success: boolean;
  token?: string;
  appMode?: AppMode;
  mfaRequired?: boolean;
  mfaState?: 'verify' | 'initial_enrollment' | 'recovery_verify' | 'recovery_enrollment' | 'replacement_enrollment';
  mfaChallenge?: string;
  expiresInSeconds?: number;
  recoveryCodes?: string[];
}

export interface AuthenticatedProfile {
  hasLocalPassword?: boolean;
  mfaStatus?: MfaStatus;
  email?: string;
  displayName?: string;
  role?: string;
  companyRole?: string;
  company?: { plan?: UserPlan; name?: string };
  plan?: UserPlan;
  companyCustomerNumber?: string | null;
  contractNumber?: string | null;
  contractStartDate?: string | Date | null;
  contractEndDate?: string | Date | null;
  roleDefaults?: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly companyRoleFallbackPermissions: Record<string, string[]> = {
    company_admin: [
      'company.dashboard.read',
      'company.company_profile.read',
      'company.company_profile.write',
      'company.properties.read',
      'company.properties.write',
      'company.properties.delete',
      'company.properties.import',
      'company.templates.read',
      'company.templates.write',
      'company.qr_poster_texts.read',
      'company.qr_poster_texts.write',
      'company.contacts.read',
      'company.contacts.write',
      'company.contacts.delete',
      'company.property_documents.read',
      'company.property_documents.write',
      'company.property_documents.delete',
      'company.emergency_instructions.write',
      'company.emergency_instructions.delete',
      'company.announcements.read',
      'company.announcements.write',
      'company.announcements.send',
      'company.announcements.delete',
      'company.ratings.read',
      'company.hospitality_ai.read',
      'company.hospitality_ai.qa.write',
      'company.integrations.read',
      'company.integrations.write',
      'company.integrations.credentials.write',
      'company.integrations.run',
      'company.integrations.delete',
      'company.users.read',
      'company.users.invite',
      'company.users.update',
      'company.users.deactivate',
      'company.users.remove',
      'company.audit_logs.read',
      'company.account.password.write',
      'company.account.deactivate_self',
      'company.account.delete_self',
      'company.desktop_link.create',
      'company.billing.read',
      'company.sign_orders.create',
      'company.sign_orders.read',
    ],
    editor: [
      'company.dashboard.read',
      'company.company_profile.read',
      'company.company_profile.write',
      'company.properties.read',
      'company.properties.write',
      'company.properties.import',
      'company.templates.read',
      'company.templates.write',
      'company.qr_poster_texts.read',
      'company.qr_poster_texts.write',
      'company.contacts.read',
      'company.contacts.write',
      'company.property_documents.read',
      'company.property_documents.write',
      'company.emergency_instructions.write',
      'company.announcements.read',
      'company.announcements.write',
      'company.announcements.send',
      'company.ratings.read',
      'company.hospitality_ai.read',
      'company.hospitality_ai.qa.write',
      'company.integrations.read',
      'company.integrations.write',
      'company.integrations.run',
      'company.account.password.write',
      'company.account.deactivate_self',
      'company.account.delete_self',
      'company.desktop_link.create',
      'company.sign_orders.create',
      'company.sign_orders.read',
    ],
    viewer: [
      'company.dashboard.read',
      'company.company_profile.read',
      'company.properties.read',
      'company.templates.read',
      'company.qr_poster_texts.read',
      'company.contacts.read',
      'company.property_documents.read',
      'company.announcements.read',
      'company.ratings.read',
      'company.hospitality_ai.read',
      'company.integrations.read',
      'company.account.password.write',
      'company.account.deactivate_self',
      'company.account.delete_self',
      'company.desktop_link.create',
      'company.sign_orders.create',
      'company.sign_orders.read',
    ],
  };

  private authTokenKey = 'authToken';
  private appModeKey = 'appMode';
  private appModeSubject = new BehaviorSubject<AppMode>(DEFAULT_APP_MODE);
  private pendingMfaChallenge: MfaChallengeResponse | null = null;

  constructor(
    private http: HttpClient,
    private companyThemeService: CompanyThemeService
  ) {
    const storedMode = this.getStoredAppMode();
    this.appModeSubject.next(storedMode);
  }

  resetPassword(token: string, newPassword: string): Observable<any> {
    return this.http.post(`${BE_API_URL}/reset-password`, { resetToken: token, newPassword }).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  resetPasswordRequest(email: string): Observable<any> {
    return this.http.post(`${BE_API_URL}/reset-password-request`, { email }).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  contactUs(name: string, email: string, phone: string, message: string, company: string): Observable<any> {
    return this.http.post(`${BE_API_URL}/contactUs`, { name, email, phone, message, company });
  }

  updatePassword(currentPassword: string, newPassword: string): Observable<any> {
    return this.http.post(`${BE_API_URL}/auth/update-password`, { currentPassword, newPassword }).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  signup(
    email: string,
    password: string,
    companyName: string,
    appMode: AppMode,
    termsAccepted: boolean,
    attribution?: RegistrationAttribution | null
  ): Observable<any> {
    return this.http.post(`${BE_API_URL}/signup`, {
      email,
      password,
      companyName,
      appMode,
      termsAccepted,
      ...(attribution ? { attribution } : {})
    }).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  verifySignupCode(
    email: string,
    code: string,
    password: string,
    companyName: string,
    appMode: AppMode,
    termsAccepted: boolean
  ): Observable<{ token: string; appMode: AppMode }> {
    return this.http.post<{ token: string; appMode: AppMode }>(`${BE_API_URL}/verify-signup-code`, {
      email,
      code,
      password,
      companyName,
      appMode,
      termsAccepted
    }).pipe(
      tap(response => {
        this.storeSession(response.token, response.appMode);  // Save the token after successful verification
      }),
      catchError(this.handleError.bind(this))
    );
  }

  // Method to handle API errors
  public handleError(error: HttpErrorResponse) {
    if (error?.error?.__handledByGlobalErrorHandler) {
      return throwError(() => error);
    }

    // Check for 401 errors specifically related to authentication
    if (error.status === 401) {
      if (error.error?.code === 'TOKEN_EXPIRED') {
        this.logout(); // Call logout method to clear storage and redirect to login
        return throwError(() => new Error('Session expired. Please log in again.'));
      } else if (error.error?.code === 'INVALID_TOKEN') {
        this.logout(); // Handle invalid token case
        return throwError(() => new Error('Session is no longer valid. Please log in again.'));
      } else if (error.error?.code === 'SESSION_REVOKED') {
        return throwError(() => new Error('Session is no longer valid. Please log in again.'));
      } else {
        // Handle other 401 errors that are not related to token expiration or invalid token
        return throwError(() => new Error('Unauthorized request. Please check your credentials.'));
      }
    }

    // Handle 403 Forbidden errors (e.g., inactive accounts or missing permissions)
    if (error.status === 403) {
      const message =
        (typeof error.error === 'object' && error.error?.message) ||
        'You do not have permission to perform this action.';
      return throwError(() => new Error(message));
    }

    // Handle 409 Conflict errors (e.g., duplicate address)
    if (error.status === 409) {
      const conflictMessage = error.error?.message || 'Address already exists for another property';
      return throwError(() => new Error(conflictMessage));
    }

    // Allow other backend errors (e.g., validation, business logic, etc.) to pass through
    if (error.status !== 401 && error.error && error.error.message) {
      return throwError(() => error.error); // Pass the entire error object
    }

    if (error.status === 404) {
      return throwError(() => new Error('Logo not found'));
    }

    // Fallback for any other type of error (e.g., server errors, network issues)
    return throwError(() => new Error(error.message || 'Server Error'));
  }

  // Method to perform login with email and password
  login(email: string, password: string): Observable<MfaChallengeResponse> {
    return this.http.post<MfaChallengeResponse>(`${BE_API_URL}/login`, { email, password }).pipe(
      tap(response => {
        // MFA challenges are opaque pre-authentication credentials and must
        // never be persisted as a browser access session.
        if (response.token) {
          this.storeSession(response.token, response.appMode);
        }
      })
    );
  }

  verifyMfa(challenge: string, code: string, method: 'totp' | 'recovery' = 'totp'): Observable<MfaChallengeResponse> {
    return this.http.post<MfaChallengeResponse>(`${BE_API_URL}/mfa/verify`, { challenge, code, method });
  }

  setupMfaEnrollment(challenge: string, bootstrapCredential?: string): Observable<{ manualKey: string; otpauthUri: string; expiresInSeconds: number }> {
    return this.http.post<{ manualKey: string; otpauthUri: string; expiresInSeconds: number }>(
      `${BE_API_URL}/mfa/enrollment/setup`,
      { challenge, ...(bootstrapCredential ? { bootstrapCredential } : {}) }
    );
  }

  confirmMfaEnrollment(challenge: string, code: string): Observable<MfaChallengeResponse> {
    return this.http.post<MfaChallengeResponse>(`${BE_API_URL}/mfa/enrollment/confirm`, { challenge, code });
  }

  startMfaReplacement(code: string): Observable<MfaChallengeResponse> {
    return this.http.post<MfaChallengeResponse>(
      `${BE_API_AUTH_URL}/mfa/replacement`,
      { code },
      { headers: this.createAuthorizationHeader() }
    );
  }

  setPendingMfaChallenge(challenge: MfaChallengeResponse): void {
    this.pendingMfaChallenge = challenge;
  }

  consumePendingMfaChallenge(): MfaChallengeResponse | null {
    const challenge = this.pendingMfaChallenge;
    this.pendingMfaChallenge = null;
    return challenge;
  }

  clearAccessSession(): void {
    this.companyThemeService.clearCompanyThemeColor();
    localStorage.removeItem(this.authTokenKey);
    sessionStorage.removeItem(this.authTokenKey);
    this.clearAppMode();
  }

  /** Create a one-time desktop login link (passwordless) */
  getDesktopLoginLink(): Observable<{ link: string }> {
    return this.http
      .post<{ link: string }>(
        `${BE_API_AUTH_URL}/desktop-link`,
        {},
        { headers: this.createAuthorizationHeader() }
      )
      .pipe(catchError(this.handleError.bind(this)));
  }

  /** Send the one-time desktop login link to the user's email */
  emailDesktopLoginLink(): Observable<{ message: string }> {
    return this.http
      .post<{ message: string }>(
        `${BE_API_AUTH_URL}/desktop-link/email`,
        {},
        { headers: this.createAuthorizationHeader() }
      )
      .pipe(catchError(this.handleError.bind(this)));
  }

  storeSession(token: string, appMode?: string | null): void {
    this.saveToken(token);
    const mode = appMode ?? this.extractAppModeFromToken(token);
    this.setAppMode(mode);
  }

  private saveToken(token: string): void {
    localStorage.setItem(this.authTokenKey, token);
  }

  // Retrieve token from local storage
  getToken(): string | null {
    return localStorage.getItem(this.authTokenKey);
  }

  getCurrentUserRole(): string | null {
    const token = this.getToken();
    if (!token) {
      return null;
    }

    const decoded = this.decodeToken(token);
    return decoded?.role || null;
  }

  getCurrentCompanyRole(): string | null {
    const token = this.getToken();
    if (!token) {
      return null;
    }

    const decoded = this.decodeToken(token);
    return decoded?.companyRole || null;
  }

  getPermissions(): string[] {
    const token = this.getToken();
    if (!token) {
      return [];
    }

    const decoded = this.decodeToken(token);
    const fromToken = Array.isArray(decoded?.permissions) ? decoded.permissions : [];
    const fallback = this.companyRoleFallbackPermissions[decoded?.companyRole || ''];
    const fromRole = Array.isArray(fallback) ? fallback : [];
    return Array.from(new Set([...fromRole, ...fromToken]));
  }

  hasPermission(permission: string): boolean {
    if (!permission) {
      return false;
    }
    if (this.isCurrentUserAdmin()) {
      return true;
    }
    return this.getPermissions().includes(permission);
  }

  hasAnyPermissions(permissions: string[]): boolean {
    if (this.isCurrentUserAdmin()) {
      return true;
    }
    const current = new Set(this.getPermissions());
    return permissions.some((permission) => current.has(permission));
  }

  hasAllPermissions(permissions: string[]): boolean {
    if (this.isCurrentUserAdmin()) {
      return true;
    }
    const current = new Set(this.getPermissions());
    return permissions.every((permission) => current.has(permission));
  }

  isCurrentUserAdmin(): boolean {
    return this.getCurrentUserRole() === 'Admin';
  }

  isCurrentCompanyUser(): boolean {
    const token = this.getToken();
    const decoded = token ? this.decodeToken(token) : null;
    return Boolean(decoded?.companyId) && decoded?.role !== 'Admin';
  }

  getUserEmail(): string | null {
    const token = this.getToken();
    if (token) {
      const decodedToken: any = jwtDecode(token);  // Assuming you are using jwt-decode library
      return decodedToken.email || null;  // Extract user email from the token
    }
    return null;
  }

  // Check if the user is authenticated
  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    const tokenPayload = this.decodeToken(token);
    const isExpired = tokenPayload && tokenPayload.exp < Date.now() / 1000;

    if (isExpired) {
      this.logout();
      return false;
    }

    return true;
  }

// Helper method to decode the JWT token
  public decodeToken(token: string): any {
    try {
      const payload = token.split('.')[1];
      const pad = '='.repeat((4 - (payload.length % 4)) % 4);
      const b64 = payload.replace(/-/g, '+').replace(/_/g, '/') + pad;
      return JSON.parse(atob(b64));
    } catch {
      return null;
    }
  }
  isLoggingOut = false;

  // Method to log out and clear the token
  logout(redirectToLogin = false): void {
    this.isLoggingOut = true;
    this.clearAccessSession();
    if (redirectToLogin) {
      window.location.assign('/login');
      return;
    }
    window.location.reload();
  }

  public createAuthorizationHeader(): HttpHeaders {
    const token = localStorage.getItem('authToken'); // Retrieve token from local storage
    return new HttpHeaders().set('Authorization', `Bearer ${token}`);
  }

  deactivateAccount(): Observable<any> {
    return this.http
      .post(`${BE_API_AUTH_URL}/deactivate-account`, {}, { headers: this.createAuthorizationHeader() })
      .pipe(catchError(this.handleError.bind(this)));
  }

  getMe(): Observable<AuthenticatedProfile> {
    return this.http
      .get<AuthenticatedProfile>(`${BE_API_AUTH_URL}/me`, { headers: this.createAuthorizationHeader() })
      .pipe(catchError(this.handleError.bind(this)));
  }

  requestAccountDeletion(payload: { reason: string; feedback?: string; confirmationText: string }): Observable<any> {
    return this.http
      .post(`${BE_API_AUTH_URL}/delete-account/request`, payload, { headers: this.createAuthorizationHeader() })
      .pipe(catchError(this.handleError.bind(this)));
  }

  setAppMode(mode: string | null | undefined): void {
    const normalized = this.normalizeAppMode(mode);
    localStorage.setItem(this.appModeKey, normalized);
    this.appModeSubject.next(normalized);
  }

  clearAppMode(): void {
    localStorage.removeItem(this.appModeKey);
    this.appModeSubject.next(DEFAULT_APP_MODE);
  }

  getAppMode(): AppMode {
    return this.appModeSubject.value;
  }

  getAppModeChanges(): Observable<AppMode> {
    return this.appModeSubject.asObservable();
  }

  private getStoredAppMode(): AppMode {
    const stored = localStorage.getItem(this.appModeKey);
    return this.normalizeAppMode(stored);
  }

  private normalizeAppMode(mode: string | null | undefined): AppMode {
    return mode === 'hospitality' ? 'hospitality' : DEFAULT_APP_MODE;
  }

  private extractAppModeFromToken(token: string): AppMode {
    const decoded = this.decodeToken(token);
    return this.normalizeAppMode(decoded?.appMode);
  }
}
